import React, { useState, useEffect } from "react";
import { Card, Form, Table, Badge, Row, Col } from "react-bootstrap";
import {
  DEFAULT_WARRANTY_PERIOD,
  WARRANTY_PERIOD_OPTIONS,
} from "../warrantyUtils";

const OrderSummary = ({
  patient,
  products,
  orderDetails,
  onUpdateOrderDetails,
  salesOptions,
}) => {
  const [accessoryPackageStatus, setAccessoryPackageStatus] = useState({});
  const [localReceiptDate, setLocalReceiptDate] = useState(
    orderDetails.receiptDate || ""
  );
  const [localWarrantyPeriod, setLocalWarrantyPeriod] = useState(
    orderDetails.warrantyPeriod || DEFAULT_WARRANTY_PERIOD
  );
  const [localSales, setLocalSales] = useState(orderDetails.sales || "");
  const [localGrandTotal, setLocalGrandTotal] = useState(
    orderDetails.grandTotal || 0
  );

  // Sync local state with props when orderDetails changes
  useEffect(() => {
    setLocalReceiptDate(orderDetails.receiptDate || "");
    setLocalWarrantyPeriod(
      orderDetails.warrantyPeriod || DEFAULT_WARRANTY_PERIOD
    );
    setLocalSales(orderDetails.sales || "");
    setLocalGrandTotal(orderDetails.grandTotal || calculateTotal());
    setAccessoryPackageStatus(orderDetails.accessoryPackageStatus || {});
  }, [
    orderDetails.receiptDate,
    orderDetails.warrantyPeriod,
    orderDetails.warrantyEndDate,
    orderDetails.sales,
    orderDetails.grandTotal,
    orderDetails.accessoryPackageStatus,
  ]);

  // Initialize grand total with calculated value if not set
  useEffect(() => {
    if (!orderDetails.grandTotal && products) {
      const calculatedTotal = calculateTotal();
      setLocalGrandTotal(calculatedTotal);
      onUpdateOrderDetails("grandTotal", calculatedTotal);
    }
  }, [products]);

  // Recalculate total when accessory package status changes
  useEffect(() => {
    const calculatedTotal = calculateTotal();
    setLocalGrandTotal(calculatedTotal);
    onUpdateOrderDetails("grandTotal", calculatedTotal);
  }, [accessoryPackageStatus]);

  const handleReceiptDateChange = (newDate) => {
    setLocalReceiptDate(newDate);
    onUpdateOrderDetails("receiptDate", newDate);
  };

  const handleWarrantyPeriodChange = (newPeriod) => {
    setLocalWarrantyPeriod(newPeriod);
    onUpdateOrderDetails("warrantyPeriod", newPeriod);
  };

  const handleSalesChange = (newSales) => {
    setLocalSales(newSales);
    onUpdateOrderDetails("sales", newSales);
  };

  const handleGrandTotalChange = (newTotal) => {
    setLocalGrandTotal(newTotal);
    onUpdateOrderDetails("grandTotal", parseFloat(newTotal) || 0);
  };

  const handleAccessoryPackageToggle = (index, isIncludedInPackage) => {
    const newPackageStatus = {
      ...accessoryPackageStatus,
      [index]: isIncludedInPackage,
    };
    
    setAccessoryPackageStatus(newPackageStatus);

    // Update order details with the new package status
    onUpdateOrderDetails("accessoryPackageStatus", newPackageStatus);
  };

  const calculateTotal = () => {
    let total = 0;

    // Add CPAP amounts (from step 2 amount field, fallback to price)
    products.cpaps.forEach((cpap) => {
      const amount = parseFloat(cpap.amount) || parseFloat(cpap.price) || 0;
      total += amount;
    });

    // Add accessory prices (only if not included in package)
    products.accessories.forEach((accessory, accessoryIndex) => {
      // Calculate actual index in the complete items array (CPAPs come first)
      const actualIndex = products.cpaps.length + accessoryIndex;
      const isIncludedInPackage = accessoryPackageStatus[actualIndex] || false;
      if (!isIncludedInPackage) {
        const unitPrice = parseFloat(accessory.unitPrice) || 0;
        const quantity = parseInt(accessory.quantity) || 1;
        total += unitPrice * quantity;
      }
    });

    return total;
  };

  const handleOrderDetailsChange = (field, value) => {
    onUpdateOrderDetails(field, value);
  };

  return (
    <div>
      <h5>Step 3: Order Summary</h5>

      <Row>
        <Col md={6}>
          {/* Patient Information */}
          <Card className="mb-3">
            <Card.Header>
              <h6>Patient Information</h6>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={6}>
                  <strong>HN:</strong> {patient && patient.hn}
                  <br />
                  <strong>Name:</strong> {patient && patient.firstnameTh}{" "}
                  {patient && patient.lastnameTh}
                  <br />
                  <strong>Phone:</strong> {(patient && patient.mobile) || "N/A"}
                </Col>
                <Col md={6}>
                  <strong>National ID:</strong>{" "}
                  {(patient && patient.nationalId) || "N/A"}
                  <br />
                  <strong>Hospital:</strong>{" "}
                  {(patient && patient.refCustomer) || "N/A"}
                  <br />
                  <strong>Hospital HN:</strong>{" "}
                  {(patient && patient.refHospital) || "N/A"}
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
        <Col md={6}>
          {/* Selected Products */}
          <Card className="mb-3">
            <Card.Header>
              <h6>Selected Products</h6>
            </Card.Header>
            <Card.Body>
              {/* CPAPs */}
              {products.cpaps.length > 0 && (
                <div className="mb-3">
                  <strong className="text-primary">
                    CPAP Devices ({products.cpaps.length})
                  </strong>
                  <Table striped size="sm" className="mt-2">
                    <thead>
                      <tr>
                        <th>Serial Number</th>
                        <th>Brand/Model</th>
                        <th>Category</th>
                        <th>Sale Package</th>
                        <th>Price</th>
                      </tr>
                    </thead>
                    <tbody>
                      {products.cpaps.map((cpap) => (
                        <tr key={cpap.id}>
                          <td>{cpap.serialNumber}</td>
                          <td>
                            {cpap.brand} {cpap.model}
                          </td>
                          <td>{cpap.type}</td>

                          <td>{orderDetails.salePackage || "N/A"}</td>
                          <td>฿{cpap.amount || cpap.price || 0}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}

              {/* Accessories */}
              {products.accessories.length > 0 && (
                <div>
                  <strong className="text-info">
                    Accessories ({products.accessories.length})
                  </strong>
                  <Table striped size="sm" className="mt-2">
                    <thead>
                      <tr>
                        <th>Incl.</th>
                        <th>Item Code</th>
                        <th>Item Name</th>
                        <th>Category</th>
                        <th>Qty</th>
                        <th>Unit Price</th>
                        <th>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {products.accessories.map((accessory, accessoryIndex) => {
                        // Calculate actual index in the complete items array (CPAPs come first)
                        const actualIndex = products.cpaps.length + accessoryIndex;
                        const isIncludedInPackage =
                          accessoryPackageStatus[actualIndex] || false;
                        const totalPrice =
                          (accessory.unitPrice || 0) *
                          (accessory.quantity || 1);

                        return (
                          <tr
                            key={accessory.id}
                            className={isIncludedInPackage ? "bg-light" : ""}
                          >
                            <td>
                              <Form.Check
                                type="checkbox"
                                checked={isIncludedInPackage}
                                onChange={(e) =>
                                  handleAccessoryPackageToggle(
                                    actualIndex,
                                    e.target.checked
                                  )
                                }
                                title="Check if this accessory is included in the package (won't add to total price)"
                              />
                            </td>
                            <td>{accessory.itemCode}</td>
                            <td>{accessory.itemName}</td>
                            <td>
                              <Badge variant="info">{accessory.category}</Badge>
                            </td>
                            <td>{accessory.quantity}</td>
                            <td>
                              {isIncludedInPackage ? (
                                <span className="text-muted">
                                  <s>฿{accessory.unitPrice || 0}</s>
                                  <small className="text-success ml-1">
                                    (Package)
                                  </small>
                                </span>
                              ) : (
                                `฿${accessory.unitPrice || 0}`
                              )}
                            </td>
                            <td>
                              {isIncludedInPackage ? (
                                <span className="text-muted">
                                  <s>฿{totalPrice}</s>
                                  <small className="text-success ml-1">
                                    (Package)
                                  </small>
                                </span>
                              ) : (
                                `฿${totalPrice}`
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </div>
              )}

              {products.cpaps.length === 0 &&
                products.accessories.length === 0 && (
                  <div className="text-center text-muted">
                    No products selected
                  </div>
                )}
            </Card.Body>
          </Card>
        </Col>

        <Col md={6}>
          {/* Order Details */}
          <Card className="mb-3">
            <Card.Header>
              <h6>Order Details</h6>
            </Card.Header>
            <Card.Body>
              <Form.Group>
                <Form.Label>Sale Order No.</Form.Label>
                <Form.Control
                  type="text"
                  value={orderDetails.receiptNo}
                  onChange={(e) =>
                    handleOrderDetailsChange("receiptNo", e.target.value)
                  }
                  placeholder="Enter sale order number"
                />
              </Form.Group>

              <Form.Group>
                <Form.Label>Receipt Date</Form.Label>
                <input
                  type="date"
                  className="form-control"
                  value={localReceiptDate}
                  onChange={(e) => handleReceiptDateChange(e.target.value)}
                />
              </Form.Group>

              <Form.Group>
                <Form.Label>Order Status</Form.Label>
                <Form.Control
                  as="select"
                  value={orderDetails.status}
                  onChange={(e) =>
                    handleOrderDetailsChange("status", e.target.value)
                  }
                >
                  <option value="เตรียมเครื่อง">เตรียมเครื่อง</option>
                  <option value="กำลังดำเนินการ">กำลังดำเนินการ</option>
                  <option value="รอเอกสาร">รอเอกสาร</option>
                  <option value="เสร็จสิ้น">เสร็จสิ้น</option>
                </Form.Control>
              </Form.Group>

              <Form.Group>
                <Form.Label>Sales Representative</Form.Label>
                <Form.Control
                  as="select"
                  value={localSales}
                  onChange={(e) => handleSalesChange(e.target.value)}
                >
                  <option value="">Select Sales Rep</option>
                  {salesOptions.map((sale) => (
                    <option key={sale.id} value={sale.name}>
                      {sale.name}
                    </option>
                  ))}
                </Form.Control>
              </Form.Group>

              <Form.Group>
                <Form.Label>Warranty Period</Form.Label>
                <Form.Control
                  as="select"
                  value={localWarrantyPeriod}
                  onChange={(e) => handleWarrantyPeriodChange(e.target.value)}
                >
                  {WARRANTY_PERIOD_OPTIONS.map((period) => (
                    <option key={period.value} value={period.value}>
                      {period.label}
                    </option>
                  ))}
                </Form.Control>
                <Form.Text className="text-muted">
                  Warranty end date is calculated automatically.
                </Form.Text>
              </Form.Group>

              <Form.Group>
                <Form.Label>Warranty Start Date</Form.Label>
                <Form.Control
                  type="date"
                  value={orderDetails.warrantyStartDate || ""}
                  onChange={(e) =>
                    handleOrderDetailsChange(
                      "warrantyStartDate",
                      e.target.value
                    )
                  }
                  required
                />
                <Form.Text className="text-muted">
                  Defaults to Receipt Date. Change it to recalculate Warranty
                  End Date.
                </Form.Text>
              </Form.Group>

              <Form.Group>
                <Form.Label>Warranty End Date</Form.Label>
                <Form.Control
                  type="date"
                  value={orderDetails.warrantyEndDate || ""}
                  readOnly
                />
                <Form.Text className="text-muted">
                  Auto-calculated from Warranty Start Date and Warranty Period.
                </Form.Text>
              </Form.Group>

              <Form.Group>
                <Form.Label>Note</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={orderDetails.note}
                  onChange={(e) =>
                    handleOrderDetailsChange("note", e.target.value)
                  }
                  placeholder="Additional notes..."
                />
              </Form.Group>
            </Card.Body>
          </Card>
        </Col>
        <Col md={6}>
          {/* Order Total */}
          <Card className="border-success">
            <Card.Header className="bg-success text-white">
              <h6 className="mb-0">Order Total</h6>
            </Card.Header>
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center">
                <strong>Total Items:</strong>
                <span>
                  {products.cpaps.length + products.accessories.length}
                </span>
              </div>
              <div className="d-flex justify-content-between align-items-center">
                <strong>CPAP Devices:</strong>
                <span>{products.cpaps.length}</span>
              </div>
              <div className="d-flex justify-content-between align-items-center">
                <strong>Accessories:</strong>
                <span>
                  {products.accessories.reduce(
                    (sum, acc) => sum + (acc.quantity || 1),
                    0
                  )}
                </span>
              </div>
              <hr />
              <div className="d-flex justify-content-between align-items-center">
                <strong className="text-success">Grand Total:</strong>
                <div className="d-flex align-items-center">
                  <span className="text-success mr-1">฿</span>
                  <Form.Control
                    type="number"
                    step="0.01"
                    min="0"
                    value={localGrandTotal}
                    onChange={(e) => handleGrandTotalChange(e.target.value)}
                    style={{
                      width: "120px",
                      textAlign: "right",
                      fontWeight: "bold",
                      color: "#28a745",
                      border: "1px solid #28a745",
                    }}
                  />
                </div>
              </div>
              <div className="text-right mt-2">
                <small className="text-muted">
                  Auto-calculated: ฿{calculateTotal().toLocaleString()}
                </small>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default OrderSummary;
